// FilePanel — the half of the file surface where a person UNDERSTANDS a file,
// DECIDES about it and later talks to it (FRONTEND-PATTERNS §Files). A file is
// a light entity, so the panel is the ContextPanel family: sections in the
// person's order — WHAT IT SHOWS first (one home for the classification: the
// assets, the topics, the caption; an open suggestion printed where its answer
// will land with its certainty; while a decision is pending the body is the
// register's ObservationCard — the reasons as its kind label, the rows as its
// subject, the answers as its actions; the review stamp last), then Facts,
// Other versions and Notes. The acts are the file's ONE registry (`FileActions.ts`,
// ADR-036 §7) drawn by the panel as every peek's: the top row Download + the Edit
// menu (Change classification · Edit caption · Rename · Move…), the footer's
// Actions door at the start (Copy link · Replace · Restore · Delete), the walk's
// pager at the end — the tile carries no ⋯; this is the door. While the picker is
// open the footer IS its Cancel · Save (`ContextPanel commit` — the picture stays
// in view and Save never scrolls out of reach).
//
// TWO HOSTS (modality follows width, the ContextPanel posture):
//   • `rail` — docked beside the shelf + flow on a wide viewport (the master-
//     detail; the rail host, files being its first consumer), the hero on top.
//   • `page` — on a small screen the panel IS the page: a second screen taking
//     the whole surface, the hero on top with the View door, the sections
//     stacked, the footer's buttons full-width, next/previous a thin bottom row.
//

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionMenu, type ResolvedAction } from './ActionMenu.js';
import { ContextPanel, PeekSheet } from './ContextPanel.js';
import { NoteComposer } from './NoteComposer.js';
import { RefusalLine } from './forms/RefusalLine.js';
import { useRefusalLine } from './forms/useRefusalLine.js';
import { useRefusalToast } from './forms/useQuickSave.js';
import { resolveFileActs } from './FileActions.js';
import { DescriptionList, Field } from './DescriptionList.js';
import { DetailSection } from './DetailSection.js';
import { ObservationCard } from './observations.js';
import { Badge } from '../ui/Badge.js';
import { EntityTimeline, TimelineFoot, type TimelineEntryData } from './EntityTimeline.js';
import { WhatItShowsPicker, type PickedNames, type PickerTopic, type WhatItShowsValue } from './WhatItShowsPicker.js';
import type { AssetCategory } from '@ems-app/schemas/assets/categories';
import { Button } from '../ui/Button.js';
import { ProgressBar } from '../ui/ProgressBar.js';
import { Tag } from '../ui/Tag.js';
import { Textarea } from '../ui/Textarea.js';
import { PeekPager } from './PeekPager.js';
import { EntityRef } from './EntityRef.js';
import { refWords } from './assetsDisplay.js';
import { icons } from '../ui/icons.js';
import { formatBytes, formatDate, formatNumber } from '../../lib/format.js';
import {
  FileGlyphTile,
  FileRow,
  fileTitle,
  formatDistance,
  type CollectionBy,
  formatDuration,
  hiddenSubjectsOf,
  namedSubjects,
  natureOf,
  needsReview,
  proposalOf,
  useTopicLabel,
  type FileNote,
  type LibraryFile,
  type LibrarySubject,
} from './filesDisplay.js';

export interface FilePanelHandlers {
  /** A "yes" on a proposal: the link is stamped confirmed, the file reviewed, one audit row. */
  onConfirm?: (file: LibraryFile, subject: LibrarySubject) => void;
  /** "It is something else" / "Say what it shows" — the person's answer; a rejected proposal is kept and marked. */
  onReclassify?: (file: LibraryFile, next: { assets: string[]; topics: string[] }, names: PickedNames) => void;
  /** "Nothing to classify" / "Keep as is" — reviewed, unchanged. */
  onNothing?: (file: LibraryFile) => void;
  onDelete?: (file: LibraryFile) => void;
  onRestore?: (file: LibraryFile) => void;
  onDownload?: (file: LibraryFile) => void;
  onOpenInTab?: (file: LibraryFile) => void;
  onRename?: (file: LibraryFile) => void;
  /** The caption of an app-uploaded photo (its display name) — a capture's words are the ticket's. */
  onEditCaption?: (file: LibraryFile) => void;
  onMove?: (file: LibraryFile) => void;
  onReplace?: (file: LibraryFile) => void;
  onOpenAtAnchor?: (file: LibraryFile) => void;
  onOpenSubject?: (subject: LibrarySubject) => void;
  /** A row of the Other versions section — opens that file in the surface. */
  onOpenVersion?: (file: LibraryFile) => void;
  /** A topic chip of the Shows line — opens that topic's COLLECTION (the surface closes). */
  onOpenSubjectCollection?: (by: CollectionBy, value: string) => void;
  /** "Copy link" — the app's own address for this file (colleagues sign in). */
  onCopyLink?: (file: LibraryFile) => void;
  /** The Notes section: add (anyone who reads the file), edit and delete one's own, delete any with the anchor's write key. */
  /** A promise is awaited: the box empties once the note lands, a rejection is its line (the text kept). */
  onAddNote?: (file: LibraryFile, text: string) => Promise<unknown> | void;
  /** A promise is awaited: the editor closes once the edit lands, a rejection is its line (the text kept). */
  onEditNote?: (file: LibraryFile, noteId: string, text: string) => Promise<unknown> | void;
  onDeleteNote?: (file: LibraryFile, noteId: string) => void;
}

export interface FilePanelProps extends FilePanelHandlers {
  file: LibraryFile;
  /** `rail` = the docked panel beside the stage (a desk); `peek` = the standard peek (a phone: the modal sheet). */
  host: 'rail' | 'peek';
  /** `peek`: open / the width at/above which the peek is a side panel (below, the sheet). */
  open?: boolean;
  wideQuery?: string;
  /** The peek was opened away from the file's anchor — Facts shows "At · <anchor>". */
  awayFromAnchor?: boolean;
  /** `review` = the walk over the inbox lens: the decision leads, the progress on top. */
  mode: 'browse' | 'review';
  /** The walk's position (1-based). */
  progress?: { index: number; total: number };
  /** The stage in hero mode — rendered above the sections. */
  hero?: ReactNode;
  canWrite: boolean;
  canReview: boolean;
  canRestore?: boolean;
  /** Other versions — a document's other versions (the older ones; the
   *  current one too when an older is open), a photo's other captures of the
   *  same subject (the stack the Latest switch folds) — newest first. */
  versions?: LibraryFile[];
  /** Which of `versions` (or the file itself) is the current / latest one. */
  currentVersionId?: string | null;
  /** The one question's material: the site whose units the picker offers (null = a place with no units), the kinds
   *  present there (the options read — the chips; the units come from the unit lookup) and the topics offered. */
  projectId: string;
  siteId: string | null;
  kinds?: readonly AssetCategory[];
  topicOptions: PickerTopic[];
  /** The walk's accelerator: the previous photo's answer. */
  previousAnswer?: { label: string; value: WhatItShowsValue } | null;
  /** The person reading — their own notes carry Edit and Delete. */
  currentUserId?: string;
  /** May add a note (the file's readers — the container's call; default on). */
  canNote?: boolean;
  /** `peek`: close. */
  onClose?: () => void;
  /** The walk over the lens: previous / next + the position ("3 of 12") — the
   *  phone's thumb-zone row is `PeekPager`; the rail shows the position alone
   *  (the stage's arrows and the keys walk there). */
  pager?: { onPrev: () => void; onNext: () => void; prevDisabled?: boolean; nextDisabled?: boolean; position?: { index: number; total: number } };
  /** A desk shows the keys (← → · Enter · C · N · Esc); the page host never does. */
  showKeys?: boolean;
  timeZone?: string;
  className?: string;
}

// The key cap — a quiet chip in the CURRENT ink (it sits inside a primary, a
// secondary or a ghost button and in the legend): no 3D key, no console.
function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="ms-1 rounded border border-current/30 px-1 font-sans text-[0.65rem] leading-4 opacity-75">
      {children}
    </kbd>
  );
}

const noop = () => {};

export function FilePanel(props: FilePanelProps) {
  const { file, host,
    open,
    wideQuery,
    awayFromAnchor = false, mode, progress, hero, canWrite, canReview, canRestore = false, projectId, siteId, kinds = [], topicOptions, previousAnswer = null, currentUserId, canNote = true, onClose, pager, showKeys = false, timeZone, className = '' } = props;
  // The screen's one zone (ADR-082 §2): the anchor's — every clock the panel prints, not only the capture's.
  const panelTz = timeZone ? { timeZone } : {};
  const { t } = useTranslation('files');
  const { t: tc } = useTranslation('common');
  const { t: ta } = useTranslation('assets');
  const topicLabel = useTopicLabel();
  const [picking, setPicking] = useState(false);
  const [picks, setPicks] = useState<WhatItShowsValue>({ assets: [], topics: [] });
  const [pickNames, setPickNames] = useState<PickedNames>(() => new Map());
  const [editingNote, setEditingNote] = useState<{ id: string; text: string } | null>(null);
  // The note editor's save: awaited — it closes once the edit lands; a refusal is the editor's line, the text kept
  // (a note gone meanwhile says so, Close the one act). A person's own note has one writer: no stamp.
  const [savingNote, setSavingNote] = useState(false);
  const noteLine = useRefusalLine({ edit: true });
  const refusedLate = useRefusalToast('note-edit');
  // The note the editor shows NOW — a save's answer that lands after a step to another file finds it closed.
  const editingNow = useRef(editingNote);
  useLayoutEffect(() => {
    editingNow.current = editingNote;
  });
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [shownId, setShownId] = useState(file.id);
  if (shownId !== file.id) {
    setShownId(file.id);
    setPicking(false);
    setPicks({ assets: [], topics: [] });
    setPickNames(new Map());
    setEditingNote(null);
    setTranscriptOpen(false);
  }
  // ONE question — "What does it show?": assets and topics in one picker (the
  // ladder: a named asset gives its category for free; the server derives it).

  const media = natureOf(file) === 'media';
  const old = Boolean(file.superseded_by); // an older version: read-only in the surface
  const capture = file.anchor.kind === 'ticket'; // a field capture — born on its ticket (a walk-up one has no visit)
  // The identity: a capture is titled by its CLASSIFICATION — the named assets,
  // else its topics (the caption is the technician's note, in Shows and Notes;
  // the phone's file name is metadata, in Facts); a document by its display
  // name, its folder in the eyebrow.
  const moment = formatDate(file.captured_at, 'datetime', panelTz) ?? undefined;
  const name = fileTitle(file, topicLabel, { voiceNote: t('tile.voiceNote', { duration: formatDuration(file.media?.duration_ms) }), moment });
  const review = mode === 'review';
  const proposal = proposalOf(file);
  const named = namedSubjects(file);
  // A named unit beyond the viewer's reach reads as its kind — never opened, never a pick.
  const unseen = hiddenSubjectsOf(file);
  const mayDecide = canReview || canWrite;
  const inbox = needsReview(file);
  const page = host === 'peek';
  // A PICTURE (a photo, a video) takes the tall box on the stage; a document or
  // a voice note shows its CARD at its own height, on the panel's surface.
  const picture = file.family === 'image' || file.family === 'video';
  // The GPS of a field capture — two lines: the FIX first (on site · "2.3 km
  // away" · no GPS — the pin toned when there is one), the CHECK-IN always on
  // the second line. A fix rides the check-in (the bot's constraint); on site
  // needs no distance — it is on site.
  const pos = file.position;
  const gpsState = pos
    ? pos.match
      ? t('panel.gpsOn')
      : pos.distance_m !== null
        ? t('panel.gpsAwayBy', { distance: formatDistance(pos.distance_m, (k, o) => t(k, o)) })
        : t('panel.gpsAway')
    : t('panel.positionNone');
  const gpsDetail = file.checked_in === null ? undefined : t(file.checked_in ? 'panel.checkedInLine' : 'panel.notCheckedInLine');
  // The picker's keys, in the CAPTURE phase so the order of listeners never
  // matters: C opens it (the walk); while it is open, Escape cancels it and the
  // walk's keys (Enter · N · the arrows) are marked handled so the library's
  // walk stays out — the peek's own Escape is untouched otherwise. Enter on a
  // button or a link is that control's own press (the footer's Save · Cancel):
  // the browser keeps its activation and the walk never hears the key (its
  // Enter would confirm the proposal the picker is overriding).
  const latest = useRef({ picking: false, canOpen: false, open: () => {} });
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const editable = e.target instanceof HTMLElement && (e.target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName));
      if (latest.current.picking) {
        if (e.key === 'Escape') {
          // A search with text clears on its first Escape (the field's own); the next one cancels.
          if (e.target instanceof HTMLInputElement && e.target.value !== '') return;
          e.preventDefault();
          setPicking(false);
        } else if (['Enter', 'n', 'N', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'j', 'k'].includes(e.key) && !(editable && e.key === 'Enter')) {
          if (e.key === 'Enter' && e.target instanceof Element && e.target.closest('button, a[href]')) e.stopPropagation();
          else if (!editable) e.preventDefault();
        }
        return;
      }
      if ((e.key === 'c' || e.key === 'C') && !editable && latest.current.canOpen) {
        e.preventDefault();
        latest.current.open();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);
  // Opening the picker — from the Edit menu, the key (C) or an answer — is opening an
  // in-place editor: the section comes into view and the cursor lands in the
  // search (WCAG 2.4.3 — focus follows the act), wherever the panel was scrolled.
  const showsRef = useRef<HTMLDivElement>(null);
  // A BORN naming (the anchor of an asset-born photo — the synthetic subject) is
  // a fact of the file's home, never an editable answer: it seeds no pick and the
  // picker does not offer that asset at all (its row in Shows stays put).
  const openPicker = () => {
    const subjects = named.filter((s) => !s.born);
    setPicks({ assets: subjects.map((s) => s.asset_id), topics: file.topics });
    // The names the file already carries — Save untouched hands them over (the toast), never a raw id.
    setPickNames(new Map(subjects.map((s) => [s.asset_id, s.label])));
    setPicking(true);
  };
  useEffect(() => {
    if (picking) showsRef.current?.scrollIntoView({ block: 'start' });
  }, [picking]);
  const savePicker = () => {
    props.onReclassify?.(file, picks, pickNames);
    setPicking(false);
  };
  // The keys read the latest render through a ref, synced after each render.
  useEffect(() => {
    latest.current = { picking, canOpen: media && mayDecide && !old && !file.deleted_at && siteId !== null, open: openPicker };
  });


  // ── What it shows — ONE home for the classification: the rows (the assets ·
  //    the topics · the caption); an open suggestion printed where its answer
  //    will land, with its certainty alone — a link's provenance (the source,
  //    the machine's way, who confirmed) is the audit's, never printed here;
  //    an auto-accepted link says so (ADR-064's evidence law). While a decision
  //    is pending the body is the register's ObservationCard — the reasons as
  //    its kind label, the rows as its subject, the answers as its actions —
  //    the same resolution idiom as the register; decided, the rows sit plain
  //    with the review stamp last. The picker opens in place. ──
  // The answers as an ANSWER LIST on both hosts — full-width rows, the word at
  // the start, the key at the end (the rail's 295px cannot hold three worded
  // answers on one line; the list reads as one idiom on the desk and the phone).
  const answers = 'flex w-full flex-col gap-1.5 [&>*]:w-full [&>*]:justify-between';
  const has = named.length > 0 || unseen.length > 0 || file.topics.length > 0;
  const pending = media && mayDecide && !old && !file.deleted_at && (proposal !== null || inbox);
  // The stamp — who decided: a person's review; else the trust dial's acceptance,
  // printed as such (ADR-064's evidence law), never as a person's word.
  const autoLink = named.find((s) => s.auto_accepted) ?? null;
  const stamp = file.reviewed_at
    ? t(has ? 'review.stamp.confirmed' : 'review.stamp.nothing', { name: file.reviewed_by?.label ?? '', date: formatDate(file.reviewed_at, 'date', panelTz) ?? '' })
    : autoLink
      ? t('review.stamp.auto', { date: formatDate(autoLink.confirmed_at, 'date', panelTz) ?? '' })
      : null;
  const reasons = file.review_reasons.length > 0 ? file.review_reasons.map((r) => t(`review.reason.${r}`)).join(' · ') : t('marks.toReview');
  const key = (cap: string) => showKeys && <Kbd>{cap}</Kbd>;
  const rows = (
    <DescriptionList>
      <Field label={t('panel.asset')}>
        {named.length > 0 || unseen.length > 0 || proposal ? (
          <span className="inline-flex flex-wrap justify-end gap-x-3 gap-y-1">
            {named.map((s) => (
              <span key={s.asset_id} className="inline-flex items-center gap-1.5">
                <EntityRef name={s.label} icon={<icons.asset size={14} aria-hidden />} onOpen={props.onOpenSubject ? () => props.onOpenSubject?.(s) : undefined} />
              </span>
            ))}
            {unseen.map((h, i) => (
              // Plain words, never a link: there is nothing the viewer may open.
              <span key={`hidden-${i}`} className="inline-flex items-center gap-1 text-[var(--color-text-secondary)]">
                <icons.asset size={14} aria-hidden />
                {refWords(h, ta)}
              </span>
            ))}
            {proposal && (
              // The open suggestion sits where its answer will land — tinted, with its certainty, never a fact.
              <span className="inline-flex items-center gap-1.5">
                <span className="text-[var(--color-warning-bold)] dark:text-[var(--color-warning)]">{proposal.label}</span>
                {proposal.confidence !== null && (
                  <span className="text-xs font-normal text-[var(--color-text-secondary)]">{t('review.sure', { value: formatNumber(Math.round(proposal.confidence * 100)) })}</span>
                )}
              </span>
            )}
          </span>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        )}
      </Field>
      <Field label={t('panel.topics')}>
        {file.topics.length > 0 ? (
          <span className="inline-flex flex-wrap items-center justify-end gap-1">
            {file.topics.map((code) => (
              <Tag key={code} onClick={props.onOpenSubjectCollection ? () => props.onOpenSubjectCollection?.('topic', code) : undefined} ariaLabel={t('panel.openCollection', { name: topicLabel(code) })}>
                {topicLabel(code)}
              </Tag>
            ))}
          </span>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        )}
      </Field>
      {file.caption && !capture && (
        <Field label={t('panel.caption')}>
          <span className="italic">“{file.caption}”</span>
        </Field>
      )}
    </DescriptionList>
  );
  const answerRow = pending && (
    <div className={answers}>
      {proposal ? (
        <>
          <Button variant="primary" onClick={() => props.onConfirm?.(file, proposal)}>
            <span>{t('review.yes')}</span>
            {key(t('review.keyCaps.enter'))}
          </Button>
          <Button variant="secondary" onClick={openPicker}>
            <span>{t('review.somethingElse')}</span>
            {key('C')}
          </Button>
          <Button variant="ghost" onClick={() => props.onNothing?.(file)}>
            <span>{t('review.nothing')}</span>
            {key('N')}
          </Button>
        </>
      ) : has ? (
        <>
          <Button variant="primary" onClick={() => props.onNothing?.(file)}>
            <span>{t('review.keep')}</span>
            {key(t('review.keyCaps.enter'))}
          </Button>
          <Button variant="secondary" onClick={openPicker}>
            <span>{t('review.change')}</span>
            {key('C')}
          </Button>
        </>
      ) : (
        <>
          <Button variant="primary" onClick={openPicker}>
            <span>{t('review.sayWhat')}</span>
            {key('C')}
          </Button>
          <Button variant="secondary" onClick={() => props.onNothing?.(file)}>
            <span>{t('review.nothing')}</span>
            {key('N')}
          </Button>
        </>
      )}
    </div>
  );
  const shows = media ? (
    <div ref={showsRef} className="scroll-mt-2">
      {/* No act in the card's corner: changing what it shows is the Edit menu's
          (Change classification); while a decision is pending the card's answers
          are the door. */}
      <DetailSection dense title={t('panel.shows')}>
      {picking && siteId ? (
        <WhatItShowsPicker
          projectId={projectId}
          siteId={siteId}
          kinds={kinds}
          topics={topicOptions}
          // A BORN naming (an asset-born photo's own anchor) is a fact of its home, never an answer: not offered.
          exclude={file.anchor.kind === 'asset' ? file.anchor.id : undefined}
          value={picks}
          onChange={(v, names) => {
            setPicks(v);
            setPickNames(names);
          }}
          suggestion={proposal ? { asset_id: proposal.asset_id, label: proposal.label, confidence: proposal.confidence } : null}
          previous={review ? previousAnswer : null}
          focusSearch
          stacked={page}
          onCancel={() => setPicking(false)}
        />
      ) : pending ? (
        <ObservationCard kind={has || proposal ? 'unverified' : 'missing'} impact={proposal || !has ? 'medium' : 'low'} kindLabel={reasons} subject={rows} actions={answerRow} />
      ) : (
        <div className="flex flex-col gap-3">
          {rows}
          {stamp && <p className="text-xs text-[var(--color-text-secondary)]">{stamp}</p>}
        </div>
      )}
    </DetailSection>
    </div>
  ) : null;

  // ── Facts — the rows the file's situation calls for (FRONTEND-PATTERNS
  //    §Files): its home when opened away from it · a document's folder (the
  //    lock beside it) · who and when (in the anchor's zone — the screen's one,
  //    a desk upload too) · the visit and the GPS of a field capture
  //    · the physical facts · the version · the deletion. No review stamp (Shows). ──
  const catalog = file.provenance === 'catalog';
  const when = formatDate(file.captured_at, 'datetime', panelTz) ?? '—';
  const typeLine = (() => {
    const fam = t(`family.${file.family}`);
    if (file.family === 'image' && file.media?.width && file.media.height) return `${fam} · ${formatNumber(file.media.width)} × ${formatNumber(file.media.height)}`;
    if ((file.family === 'video' || file.family === 'audio') && file.media?.duration_ms !== undefined) return `${fam} · ${formatDuration(file.media.duration_ms)}`;
    return fam;
  })();
  const gpsValue = (
    <span className="inline-flex items-center gap-1">
      {pos && <icons.site size={14} weight="fill" aria-hidden className={pos.match ? 'text-[var(--color-success)]' : 'text-[var(--color-warning)]'} />}
      {gpsState}
    </span>
  );
  const currentVersion = old ? (props.versions ?? []).find((v) => v.id === props.currentVersionId) : undefined;
  const versionDetail = old
    ? currentVersion
      ? t('panel.replacedBy', { version: currentVersion.version, date: formatDate(currentVersion.captured_at, 'date', panelTz) ?? '' })
      : t('panel.replaced')
    : file.older_versions > 0
      ? t('panel.olderVersions', { count: file.older_versions })
      : undefined;
  const homeIcon =
    file.anchor.kind === 'asset' ? <icons.asset size={14} aria-hidden /> : file.anchor.kind === 'site' || file.anchor.kind === 'ticket' ? <icons.site size={14} aria-hidden /> : file.anchor.kind === 'user' ? <icons.user size={14} aria-hidden /> : undefined;
  const facts = (
    <DetailSection dense title={t('panel.facts')}>
      {/* Two columns of label · value pairs when the PANEL is wide enough (a
          container query — the rail and the sheet are narrow, a wide peek is not). */}
      <DescriptionList divided={false} className="@container grid grid-cols-1 gap-x-6 @[34rem]:grid-cols-2 [&>div]:py-1.5">
        {awayFromAnchor && (
          <Field label={t(`panel.home.${file.anchor.kind}`)}>
            {/* A capture's home chip NAMES its site but carries the ticket id —
                no ticket surface exists yet (the hub is a later phase), so the
                chip is a fact, not a door. */}
            <EntityRef name={file.anchor.label} icon={homeIcon} onOpen={props.onOpenAtAnchor && !capture ? () => props.onOpenAtAnchor?.(file) : undefined} />
          </Field>
        )}
        {file.folder && (
          <Field label={t('panel.folder')}>
            <span className="inline-flex items-center gap-1">
              {file.restricted && (
                <>
                  <icons.locked size={12} aria-hidden />
                  <span className="sr-only">{t('panel.restricted')}</span>
                </>
              )}
              {t(`folders.${file.folder}`, { defaultValue: file.folder })}
            </span>
          </Field>
        )}
        {catalog ? (
          <Field label={t('panel.source')}>{t('panel.sourceCatalog')}</Field>
        ) : (
          <Field label={t('panel.uploadedBy')}>{(file.uploaded_by ?? file.visit?.person)?.label ?? '—'}</Field>
        )}
        {/* A live catalog artifact carries no landed-date on the wire (the sha is
            the identity) — the row is absent rather than "Added · —". */}
        {(!catalog || file.captured_at !== '') && (
          <Field label={t(catalog ? 'panel.added' : 'panel.uploaded')} detail={capture ? t('panel.siteTime') : undefined}>
            {when}
          </Field>
        )}
        {capture && <Field label={t('panel.visit')}>—</Field>}
        {capture && (
          <Field label={t('panel.gps')} detail={gpsDetail}>
            {gpsValue}
          </Field>
        )}
        <Field label={t('panel.type')}>{typeLine}</Field>
        <Field label={t('panel.size')}>{formatBytes(file.size_bytes)}</Field>
        {(media || file.display_name !== null) && (
          <Field label={t('panel.fileName')}>
            <span className="break-all font-normal text-[var(--color-text-secondary)]">{file.original_name}</span>
          </Field>
        )}
        {(file.version > 1 || old) && (
          <Field label={t('panel.version')} detail={versionDetail}>
            {t('panel.versionValue', { version: file.version })}
          </Field>
        )}
        {file.deleted_at && (
          <Field label={t('panel.deleted')} detail={file.deleted_by ? t('panel.deletedBy', { name: file.deleted_by.label }) : undefined}>
            {formatDate(file.deleted_at, 'datetime', panelTz) ?? '—'}
          </Field>
        )}
        {file.deleted_at && file.purge_at && <Field label={t('panel.purgedOn')}>{formatDate(file.purge_at, 'date', panelTz) ?? '—'}</Field>}
      </DescriptionList>
    </DetailSection>
  );

  // ── Other versions — the family minus this file; every row opens in the surface (an older document
  //    version is read-only there: Download alone). ──
  const versionRows = props.versions ?? [];
  const versions = versionRows.length > 0 && (
    <DetailSection dense title={t('panel.versions')}>
      <div className="-mx-3 divide-y divide-[var(--color-divider)]">
        {versionRows.map((v) => (
          <FileRow
            key={v.id}
            file={v}
            title={media ? undefined : t('panel.versionN', { n: v.version })}
            chipLabel={v.id === props.currentVersionId ? t(media ? 'panel.latest' : 'panel.current') : undefined}
            onSelect={props.onOpenVersion}
            timeZone={timeZone}
          />
        ))}
      </div>
    </DetailSection>
  );

  // ── Notes — the file's OWN words first (the technician's at capture — the
  //    journal entry's text, read-only; a voice note's transcript — machine-
  //    made, printed as such, folded when long), then the composer, then the
  //    conversation on the entity-timeline grammar (§History: one rail, the
  //    author as the moment word, the time beside it, the body under, "edited"
  //    as the foot, the ⋯ door in the entry's slot), newest first, folded past
  //    five. Anyone who reads the file may add one; a person edits and deletes
  //    their own, the anchor's write key deletes any. ──
  const notes = file.notes ?? [];
  const transcript = notes.find((n) => n.kind === 'transcript') ?? null;
  const added = [...notes.filter((n) => n.kind === 'note')].sort((a, b) => b.created_at.localeCompare(a.created_at));
  const noteCount = (capture && file.caption ? 1 : 0) + notes.length;
  const noteDoor = (n: FileNote): ResolvedAction[] => {
    const own = Boolean(currentUserId && n.author?.id === currentUserId);
    const acts: ResolvedAction[] = [];
    if (own && props.onEditNote)
      acts.push({
        id: 'edit',
        label: t('notes.edit'),
        icon: <icons.edit size={16} aria-hidden />,
        run: {
          handler: () => {
            noteLine.dismiss();
            setEditingNote({ id: n.id, text: n.text });
          },
        },
        group: 'edit',
      });
    if ((own || canWrite) && props.onDeleteNote) acts.push({ id: 'delete', label: t('notes.delete'), icon: <icons.delete size={16} aria-hidden />, tone: 'danger', run: { handler: () => props.onDeleteNote?.(file, n.id) }, group: 'danger' });
    return acts;
  };
  const closeNoteEditor = () => {
    noteLine.dismiss();
    setEditingNote(null);
  };
  const saveNote = async (noteId: string, text: string) => {
    if (savingNote) return;
    noteLine.dismiss();
    setSavingNote(true);
    try {
      await props.onEditNote?.(file, noteId, text);
      setEditingNote(null);
    } catch (err) {
      // The editor still open on this note says it; one closed meanwhile (a step to another file) says it in a toast.
      if (editingNow.current?.id === noteId) noteLine.refuse(err);
      else refusedLate(err);
    } finally {
      setSavingNote(false);
    }
  };
  const noteEditor = (n: FileNote) => (
    <div className="mt-1 flex flex-col gap-2">
      <RefusalLine line={noteLine} onClose={closeNoteEditor} />
      <Textarea
        aria-label={t('notes.edit')}
        value={editingNote?.text ?? ''}
        onChange={(e) => setEditingNote({ id: n.id, text: e.target.value })}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            // Not while the edit is on its way — its answer belongs to this editor (Cancel stands down the same).
            if (!savingNote) closeNoteEditor();
          } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && editingNote?.text.trim() && !noteLine.gone) {
            e.preventDefault();
            void saveNote(n.id, editingNote.text.trim());
          }
        }}
        rows={3}
      />
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={closeNoteEditor} disabled={savingNote}>
          {tc('actions.cancel')}
        </Button>
        <Button
          variant="primary"
          loading={savingNote}
          disabled={noteLine.gone || !editingNote?.text.trim() || editingNote.text.trim() === n.text}
          onClick={() => {
            if (!editingNote) return;
            void saveNote(n.id, editingNote.text.trim());
          }}
        >
          {tc('actions.save')}
        </Button>
      </div>
    </div>
  );
  const noteEntries: TimelineEntryData[] = added.map((n) => ({
    id: n.id,
    title: n.author?.label ?? '',
    when: formatDate(n.created_at, 'datetime', panelTz) ?? '',
    lines: editingNote?.id === n.id ? noteEditor(n) : <p className="whitespace-pre-line text-sm text-[var(--color-text-primary)]">{n.text}</p>,
    foot: n.edited_at ? <TimelineFoot parts={[t('notes.edited')]} /> : undefined,
    door: editingNote?.id !== n.id && noteDoor(n).length > 0 ? <ActionMenu label={t('notes.menu')} trigger="icon" items={noteDoor(n)} placement="bottom end" /> : undefined,
  }));
  const longTranscript = (transcript?.text.length ?? 0) > 220;
  // The note box — the shared one (keyed by the file: a step to the next starts empty).
  const composer = props.onAddNote && canNote && !old && !file.deleted_at && <NoteComposer key={file.id} onAdd={(text) => props.onAddNote?.(file, text)} />;
  // A catalog copy takes no notes (preview + download only — FRONTEND-PATTERNS
  // §Files): the section is absent, never an empty shell.
  const notesSection = !catalog && (
    <DetailSection dense title={t('notes.title')}>
      <div className="flex flex-col gap-4">
        {(capture && file.caption) || transcript ? (
          <div className="flex flex-col gap-3 rounded-md bg-[var(--color-bg-surface-alt)]/60 px-3 py-2.5">
            {capture && file.caption && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-[var(--color-text-secondary)]">{[t('notes.field'), file.visit?.person.label, formatDate(file.captured_at, 'datetime', panelTz)].filter(Boolean).join(' · ')}</span>
                <p className="text-sm text-[var(--color-text-primary)]">“{file.caption}”</p>
              </div>
            )}
            {transcript && (
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-[var(--color-text-secondary)]">{t('notes.transcriptAuto')}</span>
                <p className={`text-sm italic text-[var(--color-text-primary)] ${longTranscript && !transcriptOpen ? 'line-clamp-3' : ''}`}>{transcript.text}</p>
                {longTranscript && (
                  <Button variant="link" className="self-start" onClick={() => setTranscriptOpen((v) => !v)}>
                    {transcriptOpen ? t('notes.showLess') : t('notes.showMore')}
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : null}
        {composer}
        <EntityTimeline entries={noteEntries} foldAfter={5} showAllLabel={(n) => t('notes.showAll', { count: n })} empty={noteCount === 0 ? <p className="text-sm text-[var(--color-text-secondary)]">{t('notes.empty')}</p> : null} />
      </div>
    </DetailSection>
  );

  // ── the acts: the file's registry (a shelf row's ⋯ reads the same) — the panel draws the top row and the door ──
  const acts = resolveFileActs(file, props, { canWrite, canReview, canRestore, canDescribe: mayDecide && siteId !== null, deciding: pending, changeShows: openPicker }, t);
  // The picker's commit — the footer while it is open (Save waits for an answer).
  const commit = picking && siteId ? { onCancel: () => setPicking(false), onSave: savePicker, saveDisabled: picks.assets.length === 0 && picks.topics.length === 0 } : undefined;
  // The walk — "n of m" + ‹ › — the peek footer's pager composite at the
  // footer's END (the door at its start is the panel's; the user peek keeps
  // "Open full page" there), on BOTH hosts, a phone and the desk alike (the
  // rail's stage arrows walk too; the count lives here, never above the picture).
  const walkPosition = pager?.position ? t('stage.position', { index: formatNumber(pager.position.index), total: formatNumber(pager.position.total) }) : undefined;
  const walk = pager ? (
    <PeekPager label={walkPosition} prevLabel={t('stage.previous')} nextLabel={t('stage.next')} onPrev={pager.onPrev} onNext={pager.onNext} prevDisabled={pager.prevDisabled} nextDisabled={pager.nextDisabled} />
  ) : null;
  const status = inbox ? (
    <Badge variant="warning" appearance="subtle">
      {t('marks.toReview')}
    </Badge>
  ) : file.superseded_by ? (
    <Badge variant="neutral" appearance="subtle">
      {t('panel.olderVersion')}
    </Badge>
  ) : file.deleted_at ? (
    <Badge variant="neutral" appearance="subtle">
      {t('panel.inBin')}
    </Badge>
  ) : undefined;
  const kindWord = media ? t(`family.${file.family}`) : t('panel.document');
  // The eyebrow: the kind — and the place the file belongs to on the phone
  // sheet ("Photo · RUH-0142 Al Malqa"), where nothing sits above the header;
  // beside the stage the line above already names the place.
  const kind = page ? `${kindWord} · ${file.anchor.label}` : kindWord;
  const keys = showKeys && review && (
    <p className="text-xs text-[var(--color-text-disabled)]">
      <Kbd>←</Kbd>
      <Kbd>→</Kbd> {t('review.keys.move')} · <Kbd>{t('review.keyCaps.esc')}</Kbd> {t('review.keys.back')}
    </p>
  );
  // The walk row — PeekPager (the review-walk pager composite) on the phone:
  // the position + prev/next in the thumb zone; on the rail the position alone
  // (the stage's edge arrows and the keys already walk — no third pair). The
  // review's progress bar rides under either.
  const progressBar = progress && (
    <ProgressBar ariaLabel={t('review.progressLabel')} value={Math.round(((progress.index - 1) / Math.max(1, progress.total)) * 100)} className="w-full" />
  );

  // A step swaps the content in place — no motion (DESIGN-TOKENS §Motion: motion
  // is overlay-only; the sheet's drawer animation plays on open and close alone).
  const sections = (
    <div key={file.id} className="flex flex-col gap-3">
      {shows}
      {facts}
      {versions}
      {notesSection}
      {keys}
    </div>
  );

  if (host === 'rail') {
    return (
      <ContextPanel host="rail" leading={<FileGlyphTile file={file} size="sm" thumb={false} />} kind={kind} title={name} status={status} acts={acts} footer={walk} commit={commit} className={className}>
        {progressBar}
        {/* The hero's top bleed exists to reach the sheet's edge when it is the
            FIRST content — with the walk's progress bar above it, bleeding would
            pull the picture OVER the bar (the review-behind-the-media bug). */}
        {hero && <div key={`hero-${file.id}`} className={`-mx-5 ${progress ? '' : '-mt-4'} ${picture ? 'aspect-[4/3] max-h-[18rem]' : ''}`}>{hero}</div>}
        {sections}
      </ContextPanel>
    );
  }

  // ── the peek host: the standard peek (the modal sheet on a phone) — the same
  //    header as every peek, the hero then the sections, the door at the
  //    footer's start, the walk's ‹ › at its end. No peek host renders a file,
  //    so the panel brings its own sheet; a step swaps the face inside it. ──
  return (
    <PeekSheet overlay="file" open={open ?? true} onClose={onClose ?? noop} wideQuery={wideQuery} className={className}>
      <ContextPanel host="peek" overlay="file" onClose={onClose} wideQuery={wideQuery} leading={<FileGlyphTile file={file} size="sm" thumb={false} />} kind={kind} title={name} status={status} acts={acts} footer={walk} commit={commit}>
        {progressBar}
        {/* No top bleed under the walk's progress bar — same rule as the rail branch. */}
        {hero && <div key={`hero-${file.id}`} className={`-mx-5 ${progress ? '' : '-mt-4'} mb-1 shrink-0 overflow-hidden ${picture ? 'h-[46dvh]' : ''}`}>{hero}</div>}
        {sections}
      </ContextPanel>
    </PeekSheet>
  );
}
